// Hit testing — waypoint and segment cursor tests. Tolerance is in
// screen pixels and divided by camera zoom so the user always gets the
// same on-screen target size regardless of how zoomed in they are.

import { curvePointAt, curvePointAtOpen } from '../../shared/spline.js';
import { closestPointOnSegment } from '../../shared/geometry.js';
import { markerLineEndpoints, trackPerpendicularAt } from '../../shared/markers.js';
import { activeWaypoints, activeAlpha, activeIsClosed, trackAlpha } from './state.js';

// Label badge offset (m) — matches the editor renderer's offset so the
// hit zone aligns with where the badge actually paints. Updated together
// if the renderer offset ever changes.
const LABEL_BADGE_OFFSET_M = 12;
const LABEL_HIT_PX = 18;

export const WAYPOINT_HIT_PX = 11;
// Segment-insert tolerance is in screen pixels. Both the spline-
// centerline test and the wall/patch tests now compute the nearest
// point on each LINE SEGMENT between sampled vertices (via
// closestPointOnSegment), so the effective resolution is exact at
// any zoom level — earlier point-only sampling left ~60px gaps
// between consecutive samples when zoomed in close, which is what
// made the "+" indicator only appear in tiny pockets. With segment-
// based distance, 18px is a comfortable click target at every zoom.
export const SEGMENT_HIT_PX  = 18;

// Test against any waypoint array. Returns the index hit, or -1.
export function hitTestWaypointInArray(state, wps, worldPos) {
  const tol = WAYPOINT_HIT_PX / state.camera.zoom;
  for (let i = wps.length - 1; i >= 0; i--) {
    const dx = wps[i].x - worldPos.x;
    const dy = wps[i].y - worldPos.y;
    if (Math.hypot(dx, dy) < tol) return i;
  }
  return -1;
}

// Walk a Catmull-Rom curve in line-segment chunks (SUBS sub-segments
// per authored segment) and return the world point on the curve
// closest to worldPos. Returns { dist, segIdx, t, point } or null if
// the curve is too short to densify. No tolerance gate — callers
// apply their own. Shared by the centerline / wall / patch / project
// hit tests so a fix to the sampling math lands once.
function nearestPointOnSampledCurve(wps, closed, alpha, worldPos, SUBS) {
  if (wps.length < 2) return null;
  if (closed && wps.length < 3) return null;
  const evalCurve = closed ? curvePointAt : curvePointAtOpen;
  const segCount = closed ? wps.length : wps.length - 1;
  let best = { dist: Infinity, segIdx: -1, t: 0, point: null };
  // Per authored segment, walk SUBS sub-segments. Both endpoints of
  // each sub-segment belong to the same authored segment (Catmull-Rom
  // is continuous at the joints, so we don't need cross-segment line
  // segments — the j=SUBS sample of segment i and the j=0 sample of
  // segment i+1 are the same world point).
  for (let i = 0; i < segCount; i++) {
    let pPrev = evalCurve(wps, i, 0, alpha);
    for (let j = 1; j <= SUBS; j++) {
      const t = j / SUBS;
      const pNext = evalCurve(wps, i, t, alpha);
      const r = closestPointOnSegment(worldPos, pPrev, pNext);
      if (r.dist < best.dist) {
        const tPrev = (j - 1) / SUBS;
        const hitT = tPrev + (t - tPrev) * r.t;
        best = { dist: r.dist, segIdx: i, t: hitT, point: r.point };
      }
      pPrev = pNext;
    }
  }
  return best.point ? best : null;
}

export function hitTestWaypoint(state, worldPos) {
  return hitTestWaypointInArray(state, activeWaypoints(state), worldPos);
}

// Generic spline hit test — walks any waypoint array, densifying the
// curve at SUBS samples per segment, then measures cursor distance
// against each LINE SEGMENT of the densified polyline (not just the
// sampled vertices). The line-segment math fills in everything
// between samples, so the cursor sees the exact spline path at any
// zoom level — sampling resolution only affects how closely the
// straight-line approximation hugs the curve, not whether the user
// can hit the line at all.
//
// Returns the (segIdx, t) of the segment + parameter where the curve
// passes closest, plus `point` (the projected position) and `dist`
// (distance in world units).
export function hitTestSplineCenterline(state, worldPos, wps, closed, alpha) {
  const tol = SEGMENT_HIT_PX / state.camera.zoom;
  const best = nearestPointOnSampledCurve(wps, closed, alpha, worldPos, 24);
  return best && best.dist < tol ? best : null;
}

export function hitTestCenterline(state, worldPos) {
  return hitTestSplineCenterline(state, worldPos, activeWaypoints(state), activeIsClosed(state), activeAlpha(state));
}

// Always tests against the main trackline. Required for placing
// markers (S/F, sectors, pit in/out) — these are positioned on the
// racing surface, not on whichever spline happens to be the current
// editing target.
export function hitTestMainTrackCenterline(state, worldPos) {
  return hitTestSplineCenterline(
    state,
    worldPos,
    state.trackData.trackline.waypoints,
    true,
    trackAlpha(state),
  );
}

// Like hitTestMainTrackCenterline, but with NO tolerance gate — always
// returns the closest sampled point on the trackline (when there are
// enough waypoints to densify). Used by markers + labels placement so
// a click anywhere on the canvas snaps to the nearest centerline
// point rather than requiring the user to click directly on the line.
// Pairs with the hover-preview rendering so the user sees where the
// next click will commit.
export function projectToMainTrackCenterline(state, worldPos) {
  const wps = state.trackData.trackline.waypoints;
  return nearestPointOnSampledCurve(wps, true, trackAlpha(state), worldPos, 24);
}

// Walls-mode helpers: search across ALL walls, not just the active
// one. The active wall is checked first so its waypoints/segments
// take precedence when stacked.

export function hitTestAnyWallWaypoint(state, worldPos) {
  if (state.mode !== 'walls') return null;
  const walls = state.trackData.walls || [];
  if (state.activeWallIdx >= 0) {
    const i = hitTestWaypointInArray(state, walls[state.activeWallIdx]?.waypoints ?? [], worldPos);
    if (i >= 0) return { wallIdx: state.activeWallIdx, waypointIdx: i };
  }
  for (let wi = 0; wi < walls.length; wi++) {
    if (wi === state.activeWallIdx) continue;
    const i = hitTestWaypointInArray(state, walls[wi].waypoints, worldPos);
    if (i >= 0) return { wallIdx: wi, waypointIdx: i };
  }
  return null;
}

// Marker line hit test — returns the marker name ('startFinish' /
// 'startLine' / 'sector0' / 'sector1') if the cursor is within tol of
// that marker's visible track-width line, or null. Used in markers
// mode for switching the active marker by clicking its line.
const MARKER_HIT_PX = 8;
const MARKER_NAMES = ['startFinish', 'startLine', 'sector0', 'sector1'];

function getMarker(state, name) {
  const td = state.trackData;
  if (name === 'startFinish') return td.startFinish;
  if (name === 'startLine')   return td.startLine;
  if (name === 'sector0')     return td.sectors?.[0] ?? null;
  if (name === 'sector1')     return td.sectors?.[1] ?? null;
  return null;
}

// Pit-marker hit test. Entry / exit live on the MAIN trackline;
// startFinish lives on the PIT LANE spline — different geometry, same
// hit-test loop.
export function hitTestPitMarker(state, worldPos) {
  const pl = state.trackData.pitLane;
  if (!pl) return null;
  const tol = MARKER_HIT_PX / state.camera.zoom;
  const direction = state.trackData.direction ?? 'cw';
  const trackline = state.trackData.trackline;
  let best = { dist: Infinity, name: null };
  for (const name of ['entry', 'exit']) {
    const m = pl[name];
    if (!m) continue;
    const ends = markerLineEndpoints(m, trackline, direction);
    if (!ends) continue;
    const r = closestPointOnSegment(worldPos, ends.a, ends.b);
    if (r.dist < best.dist) best = { dist: r.dist, name };
  }
  // Pit-spline markers — pit S/F + speed-zone gates. Same hit-test
  // loop as entry / exit, but markerLineEndpoints uses the pit-lane
  // spline as its reference, since these markers live in pit-lane
  // (not main-track) space.
  for (const name of ['startFinish', 'speedZoneStart', 'speedZoneEnd']) {
    const m = pl[name];
    if (!m) continue;
    const ends = markerLineEndpoints(m, pl, 'cw', false);
    if (!ends) continue;
    const r = closestPointOnSegment(worldPos, ends.a, ends.b);
    if (r.dist < best.dist) best = { dist: r.dist, name };
  }
  return best.dist < tol ? best.name : null;
}

export function hitTestMarkerLine(state, worldPos) {
  const tol = MARKER_HIT_PX / state.camera.zoom;
  const direction = state.trackData.direction ?? 'cw';
  const trackline = state.trackData.trackline;
  let best = { dist: Infinity, name: null };
  for (const name of MARKER_NAMES) {
    const m = getMarker(state, name);
    if (!m) continue;
    const ends = markerLineEndpoints(m, trackline, direction);
    if (!ends) continue;
    const r = closestPointOnSegment(worldPos, ends.a, ends.b);
    if (r.dist < best.dist) best = { dist: r.dist, name };
  }
  return best.dist < tol ? best.name : null;
}

export function hitTestAnyPatchVertex(state, worldPos) {
  if (state.mode !== 'surfaces') return null;
  const patches = state.trackData.surfacePatches || [];
  if (state.activePatchIdx >= 0) {
    const i = hitTestWaypointInArray(state, patches[state.activePatchIdx]?.polygon ?? [], worldPos);
    if (i >= 0) return { patchIdx: state.activePatchIdx, vertexIdx: i };
  }
  for (let pi = 0; pi < patches.length; pi++) {
    if (pi === state.activePatchIdx) continue;
    const i = hitTestWaypointInArray(state, patches[pi].polygon, worldPos);
    if (i >= 0) return { patchIdx: pi, vertexIdx: i };
  }
  return null;
}

export function hitTestAnyPatchEdge(state, worldPos) {
  if (state.mode !== 'surfaces') return null;
  const patches = state.trackData.surfacePatches || [];
  const tol = SEGMENT_HIT_PX / state.camera.zoom;
  let best = { dist: Infinity };

  for (let pi = 0; pi < patches.length; pi++) {
    const patch = patches[pi];
    const closed = patch.closed !== false;
    const alpha = patch.alpha ?? 0.5;
    const r = nearestPointOnSampledCurve(patch.polygon, closed, alpha, worldPos, 24);
    if (r && r.dist < best.dist) best = { ...r, patchIdx: pi };
  }
  return best.dist < tol ? best : null;
}

// Hit test for label badges (corners) and area midpoint markers in
// labels mode. Returns the index in track.labels of the hit, or -1.
// Both kinds use the same screen-px tolerance — the renderer paints
// corner badges and area mid-banners at a similar size, and the
// authoring affordance is "click anywhere on the visible label glyph
// to select".
export function hitTestLabel(state, worldPos) {
  if (state.mode !== 'labels') return -1;
  const labels = state.trackData.labels || [];
  if (labels.length === 0) return -1;
  const wps = state.trackData.trackline.waypoints;
  if (wps.length < 3) return -1;
  const alpha = state.trackData.trackline.alpha ?? 0.5;
  const tol = LABEL_HIT_PX / state.camera.zoom;

  let bestIdx = -1, bestDist = Infinity;
  for (let i = 0; i < labels.length; i++) {
    const L = labels[i];
    if (!L) continue;
    let cx, cy;
    if (L.kind === 'corner') {
      const p = curvePointAt(wps, L.segmentIdx, L.t, alpha);
      const perp = trackPerpendicularAt(wps, L.segmentIdx, L.t, alpha);
      cx = p.x + perp.x * LABEL_BADGE_OFFSET_M;
      cy = p.y + perp.y * LABEL_BADGE_OFFSET_M;
    } else if (L.kind === 'area') {
      // Area picks via the start tick, end tick, or banner midpoint —
      // any of the three spots that visually represent the area.
      const s = curvePointAt(wps, L.startSegIdx, L.startT, alpha);
      const e = curvePointAt(wps, L.endSegIdx,   L.endT,   alpha);
      const dStart = Math.hypot(s.x - worldPos.x, s.y - worldPos.y);
      const dEnd   = Math.hypot(e.x - worldPos.x, e.y - worldPos.y);
      const localBest = Math.min(dStart, dEnd);
      // Midpoint banner: cheap approximation — use the midpoint of the
      // two end positions (good enough for hit testing; geometry has
      // the precise midArc-derived position for the renderer).
      const midX = (s.x + e.x) / 2;
      const midY = (s.y + e.y) / 2;
      const dMid = Math.hypot(midX - worldPos.x, midY - worldPos.y);
      const localBest2 = Math.min(localBest, dMid);
      if (localBest2 < bestDist) { bestDist = localBest2; bestIdx = i; }
      continue;
    } else {
      continue;
    }
    const d = Math.hypot(cx - worldPos.x, cy - worldPos.y);
    if (d < bestDist) { bestDist = d; bestIdx = i; }
  }
  return bestDist < tol ? bestIdx : -1;
}

export function hitTestAnyWallCenterline(state, worldPos) {
  if (state.mode !== 'walls') return null;
  const walls = state.trackData.walls || [];
  const tol = SEGMENT_HIT_PX / state.camera.zoom;
  let best = { dist: Infinity };

  for (let wi = 0; wi < walls.length; wi++) {
    const w = walls[wi];
    const alpha = w.alpha ?? 0.5;
    const r = nearestPointOnSampledCurve(w.waypoints, !!w.closed, alpha, worldPos, 24);
    if (r && r.dist < best.dist) best = { ...r, wallIdx: wi };
  }
  return best.dist < tol ? best : null;
}
